import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";
import { EMPTY_STRING_ARRAY } from "../constants";
import { aggregateRows } from "../core/aggregateRows";
import { collectGroupInfo } from "../core/collectGroupInfo";
import { buildGroupFieldTemplates } from "../core/groupFieldTemplates";
import { areFieldListsEqual } from "../utils/compositeKey";
import { sortRows } from "../utils/sort";
import type { ChartData, ChartDataParams } from "../types";

export function FormatChartData({
  aggregationKeys,
  averageKeys,
  averageDuplicationKeys,
  groupBy,
  keys,
  priceKey = "",
  volumeKey,
  tableKeys,
  sortby,
  reverseSort,
  data,
}: ChartDataParams): ChartData {
  const averageKeyList = averageKeys ?? EMPTY_STRING_ARRAY;
  const averageKeySet =
    averageKeyList.length > 0 ? new Set(averageKeyList) : null;
  const filteredAggregationKeys = averageKeySet
    ? aggregationKeys.filter((key) => !averageKeySet.has(key))
    : aggregationKeys;
  const { distinctGroupCombinations, groupByKey, groupKeysByRow } =
    collectGroupInfo(data, groupBy);

  const units: string[] = [
    priceKey,
    volumeKey,
    ...filteredAggregationKeys,
    ...averageKeyList,
  ];

  const { orderedTemplates, templatesByGroup } = buildGroupFieldTemplates(
    distinctGroupCombinations,
    filteredAggregationKeys,
    averageKeyList,
    priceKey,
    volumeKey,
  );

  const finalRes = aggregateRows({
    data,
    keyFields: keys,
    groupBy,
    groupKeysByRow,
    aggregationKeys: filteredAggregationKeys,
    averageKeys: averageKeyList,
    averageDuplicationKeys,
    priceKey,
    volumeKey,
    orderedTemplates,
    templatesByGroup,
  });

  sortRows(finalRes, sortby, reverseSort);

  let tableData: Array<Record<string, any>> = [];

  if (tableKeys && tableKeys.length > 0) {
    if (areFieldListsEqual(keys, tableKeys)) {
      tableData = finalRes.slice();
    } else {
      tableData = aggregateRows({
        data,
        keyFields: tableKeys,
        groupBy,
        groupKeysByRow,
        aggregationKeys: filteredAggregationKeys,
        averageKeys: averageKeyList,
        averageDuplicationKeys,
        priceKey,
        volumeKey,
        orderedTemplates,
        templatesByGroup,
      });

      sortRows(tableData, sortby, reverseSort);
    }
  }

  const groupCount = distinctGroupCombinations.length;
  const keysLabels = new Array<string>(groupCount);
  for (let i = 0; i < groupCount; i += 1) {
    keysLabels[i] = normalizeDisplayLabelWithoutUndefined(
      distinctGroupCombinations[i],
    );
  }

  return {
    keys: distinctGroupCombinations,
    unit_keys: units,
    keysLabels,
    group_by: groupByKey,
    data: finalRes,
    tableData: tableData,
  };
}
